import type { ReactNode } from 'react';
import { FormControlLabel } from '@mui/material';
import { useFormContext } from 'react-hook-form';

import { Switch } from '@theguarantors/tg-package-material-ui';

import { createDataTestId } from '@/utils/createDataTestId';

export type LeaseSwitchInputProps = {
  name: string;
  label?: ReactNode;
  disabled?: boolean;
  onChangeValue?: (checked: boolean) => void;
};

// Boolean toggle bound to RHF via `useFormContext`. Stores a boolean in form
// state (unlike the string-valued primitives).
export function LeaseSwitchInput({
  name,
  label,
  disabled = false,
  onChangeValue,
}: LeaseSwitchInputProps) {
  const { watch, setValue, trigger } = useFormContext();
  const checked = Boolean(watch(name));
  const dataTestId = createDataTestId({ inputString: name, suffix: 'switch' });

  const handleChange = (_event: React.ChangeEvent<HTMLInputElement>, nextChecked: boolean) => {
    setValue(name, nextChecked, { shouldValidate: true, shouldDirty: true });
    trigger(name);
    onChangeValue?.(nextChecked);
  };

  return (
    <FormControlLabel
      control={
        <Switch
          checked={checked}
          onChange={handleChange}
          disabled={disabled}
          id={dataTestId}
          data-testid={dataTestId}
        />
      }
      label={label ?? ''}
    />
  );
}
